import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { storageApi } from "@/services/api-v2.service";
import type {
  StorageStats,
  StorageSettingsUpdate,
  TestConnectionRequest,
} from "@/types/api-v2";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

export default function StorageManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedStorageType, setSelectedStorageType] = useState("local");

  // Fetch storage statistics
  const { data: storageStats, isLoading: statsLoading } = useQuery<StorageStats>({
    queryKey: ["admin", "storage", "stats"],
    queryFn: () => storageApi.getStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch storage settings
  const { data: storageSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ["admin", "storage", "settings"],
    queryFn: () => storageApi.getSettings(),
  });

  // Обновляем selectedStorageType при загрузке настроек из API
  useEffect(() => {
    if (storageSettings?.storage_type) {
      setSelectedStorageType(storageSettings.storage_type);
    }
  }, [storageSettings]);

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async (settings: StorageSettingsUpdate) => {
      return await storageApi.updateSettings(settings);
    },
    onSuccess: () => {
      toast({
        title: "Настройки сохранены",
        description: "Конфигурация хранилища успешно обновлена",
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "storage", "settings"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка сохранения",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: async (config: TestConnectionRequest) => {
      return await storageApi.testConnection(config);
    },
    onSuccess: (data) => {
      toast({
        title: data.success ? "Подключение успешно" : "Ошибка подключения",
        description: data.message,
        variant: data.success ? "default" : "destructive",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка тестирования",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Cleanup mutation
  const cleanupMutation = useMutation({
    mutationFn: async () => {
      return await storageApi.cleanup();
    },
    onSuccess: (data) => {
      const description = data.files_removed === 0 
        ? `Временные файлы не найдены. Сохранено ${data.files_preserved} системных файлов`
        : `Удалено ${data.files_removed} временных файлов, сохранено ${data.files_preserved} системных файлов. Освобождено: ${formatBytes(data.size_freed)}`;
      
      toast({
        title: "Очистка завершена",
        description,
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "storage", "stats"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка очистки",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Check integrity mutation
  const checkIntegrityMutation = useMutation({
    mutationFn: async () => {
      return await storageApi.checkIntegrity();
    },
    onSuccess: (data) => {
      toast({
        title: "Проверка целостности завершена",
        description: `Проверено файлов: ${data.total_files}, найдено ошибок: ${data.errors}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка проверки целостности",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Optimize storage mutation
  const optimizeMutation = useMutation({
    mutationFn: async () => {
      return await storageApi.optimize();
    },
    onSuccess: (data) => {
      const parts = [];
      if (data.duplicates_removed > 0) {
        parts.push(`удалено ${data.duplicates_removed} дубликатов`);
      }
      if (data.integrity_issues_fixed > 0) {
        parts.push(`исправлено ${data.integrity_issues_fixed} проблем целостности`);
      }
      if (data.space_freed > 0) {
        parts.push(`освобождено ${formatBytes(data.space_freed)}`);
      }
      
      const description = data.message || (parts.length > 0 ? 
        parts.join(', ') : 
        'Проблемы не найдены');
      
      toast({
        title: "Оптимизация завершена",
        description,
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "storage", "stats"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка оптимизации",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    
      const settings: StorageSettingsUpdate = {
        storage_type: selectedStorageType,
        local_path: formData.get("localPath") as string,
        max_file_size: parseInt(formData.get("maxFileSize") as string) * 1024 * 1024, // Конвертируем МБ в байты
        allowed_types: formData.get("allowedTypes") as string,
        // Передаем s3 только если выбран тип minio или s3
        s3: (selectedStorageType === "minio" || selectedStorageType === "s3") ? {
          endpoint: formData.get("s3Endpoint") as string,
          access_key: formData.get("s3AccessKey") as string,
          secret_key: formData.get("s3SecretKey") as string,
          bucket: formData.get("s3Bucket") as string,
          region: formData.get("s3Region") as string,
          force_path_style: formData.get("s3ForcePathStyle") === "on",
        } : undefined,
      };

    saveSettingsMutation.mutate(settings);
  };

  const handleTestConnection = () => {
    const config: TestConnectionRequest = {
      storage_type: selectedStorageType,
      s3_config: storageSettings?.s3 ? {
        endpoint: storageSettings.s3.endpoint || "",
        access_key: storageSettings.s3.access_key || "",
        secret_key: storageSettings.s3.secret_key || "",
        bucket: storageSettings.s3.bucket || "",
        region: storageSettings.s3.region || "us-east-1",
        force_path_style: storageSettings.s3.force_path_style || false,
      } : undefined,
    };
    testConnectionMutation.mutate(config);
  };

  if (statsLoading || settingsLoading) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        Загрузка...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Storage Provider Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Настройки хранилища</CardTitle>
          <CardDescription>
            Выберите провайдера хранилища для файлов системы
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div>
              <Label htmlFor="storageType">Тип хранилища</Label>
              <Select value={selectedStorageType} onValueChange={setSelectedStorageType}>
                <SelectTrigger className="border-border bg-background">
                  <SelectValue placeholder="Выберите тип хранилища" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">Локальное хранилище (uploads)</SelectItem>
                  <SelectItem value="s3">Amazon S3</SelectItem>
                  <SelectItem value="minio">MinIO</SelectItem>
                  <SelectItem value="r2">Cloudflare R2</SelectItem>
                  <SelectItem value="gcs">Google Cloud Storage</SelectItem>
                  <SelectItem value="azure">Azure Blob Storage</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Local Storage Settings */}
            {selectedStorageType === "local" && (
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <h4 className="font-medium mb-3 text-foreground">Настройки локального хранилища</h4>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="localPath">Папка для хранения файлов</Label>
                    <Input 
                      id="localPath"
                      name="localPath"
                      defaultValue={storageSettings?.local_path || "./uploads"}
                      placeholder="Путь к папке uploads"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Относительный или абсолютный путь к папке для хранения файлов
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="maxFileSize">Максимальный размер файла (MB)</Label>
                    <Input 
                      id="maxFileSize"
                      name="maxFileSize"
                      type="number"
                      defaultValue={Math.round((storageSettings?.max_file_size || 52428800) / 1024 / 1024)}
                      placeholder="50"
                    />
                  </div>
                  <div>
                    <Label htmlFor="allowedTypes">Разрешённые типы файлов</Label>
                    <Input 
                      id="allowedTypes"
                      name="allowedTypes"
                      defaultValue={storageSettings?.allowed_types || ".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"}
                      placeholder="Список расширений файлов"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* S3 Storage Settings */}
            {selectedStorageType !== "local" && (
              <div className="rounded-lg border border-border bg-muted/20 p-4">
                <h4 className="font-medium mb-3 text-foreground">Настройки S3-совместимого хранилища</h4>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="s3Endpoint">Endpoint URL</Label>
                    <Input 
                      id="s3Endpoint"
                      name="s3Endpoint"
                      defaultValue={storageSettings?.s3?.endpoint || ""}
                      placeholder="https://s3.amazonaws.com или https://minio.example.com"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="s3AccessKey">Access Key</Label>
                      <Input 
                        id="s3AccessKey"
                        name="s3AccessKey"
                        placeholder="Ключ доступа"
                        type="password"
                        defaultValue={storageSettings?.s3?.access_key || ""}
                      />
                    </div>
                    <div>
                      <Label htmlFor="s3SecretKey">Secret Key</Label>
                      <Input 
                        id="s3SecretKey"
                        name="s3SecretKey"
                        placeholder="Секретный ключ"
                        type="password"
                        defaultValue={storageSettings?.s3?.secret_key || ""}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="s3Bucket">Bucket Name</Label>
                      <Input 
                        id="s3Bucket"
                        name="s3Bucket"
                        defaultValue={storageSettings?.s3?.bucket || ""}
                        placeholder="имя-бакета"
                      />
                    </div>
                    <div>
                      <Label htmlFor="s3Region">Region</Label>
                      <Input 
                        id="s3Region"
                        name="s3Region"
                        defaultValue={storageSettings?.s3?.region || "us-east-1"}
                        placeholder="us-east-1"
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="s3ForcePathStyle" 
                      name="s3ForcePathStyle"
                      defaultChecked={storageSettings?.s3?.force_path_style || false}
                    />
                    <Label htmlFor="s3ForcePathStyle">
                      Использовать path-style URL (для MinIO)
                    </Label>
                  </div>
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <Button type="submit" disabled={saveSettingsMutation.isPending}>
                {saveSettingsMutation.isPending ? "Сохранение..." : "Сохранить настройки"}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleTestConnection}
                disabled={testConnectionMutation.isPending}
              >
                {testConnectionMutation.isPending ? "Тестирование..." : "Тестировать подключение"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Storage Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Статистика хранилища</CardTitle>
          <CardDescription>
            Агрегаты filestore (БД) и папка uploads; «карты» — эвристика по пути + сироты вне БД
          </CardDescription>
        </CardHeader>
        <CardContent>
          {storageStats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <h4 className="font-medium text-muted-foreground">Общий размер</h4>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatBytes(storageStats.total_size)}</p>
                  <p className="text-sm text-muted-foreground">Всего файлов: {storageStats.total_files}</p>
                  <p className="text-xs text-muted-foreground mt-1">Диск: {storageStats.disk_usage}%</p>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <h4 className="font-medium text-muted-foreground">Вложения (filestore, ready)</h4>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatBytes(storageStats.object_files.size)}</p>
                  <p className="text-sm text-muted-foreground">Файлов: {storageStats.object_files.count}</p>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <h4 className="font-medium text-muted-foreground">Карты/тайлы (сироты, эвристика)</h4>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{formatBytes(storageStats.map_files.size)}</p>
                  <p className="text-sm text-muted-foreground">Файлов: {storageStats.map_files.count}</p>
                </div>
              </div>
              {storageStats.filestore?.untracked_on_disk != null &&
                (storageStats.filestore.untracked_on_disk.count > 0 ||
                  storageStats.filestore.untracked_on_disk.size > 0) && (
                  <p className="text-xs text-muted-foreground">
                    Вне БД в uploads: {storageStats.filestore.untracked_on_disk.count} файлов,{" "}
                    {formatBytes(storageStats.filestore.untracked_on_disk.size)} (карточка «карты» — подмножество
                    по пути map/tile)
                  </p>
                )}
            </div>
          ) : (
            <div className="text-center py-4">Загрузка статистики...</div>
          )}
        </CardContent>
      </Card>

      {/* File Management */}
      <Card>
        <CardHeader>
          <CardTitle>Управление файлами</CardTitle>
          <CardDescription>
            Инструменты для управления загруженными файлами
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex space-x-3">
            <Button 
              variant="outline" 
              onClick={() => cleanupMutation.mutate()}
              disabled={cleanupMutation.isPending}
            >
              {cleanupMutation.isPending ? "Очистка..." : "Очистить временные файлы"}
            </Button>
            <Button 
              variant="outline"
              onClick={() => checkIntegrityMutation.mutate()}
              disabled={checkIntegrityMutation.isPending}
            >
              {checkIntegrityMutation.isPending ? "Проверка..." : "Проверить целостность"}
            </Button>
            <Button 
              variant="outline"
              onClick={() => optimizeMutation.mutate()}
              disabled={optimizeMutation.isPending}
            >
              {optimizeMutation.isPending ? "Оптимизация..." : "Оптимизировать хранилище"}
            </Button>
          </div>
          
          <div className="rounded-lg border border-border bg-muted/20 p-4">
            <h4 className="font-medium mb-3 text-foreground">Автоматическая очистка</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox id="autoCleanup" defaultChecked />
                <Label htmlFor="autoCleanup">
                  Автоматически удалять файлы без привязки к объектам
                </Label>
              </div>
              <div>
                <Label htmlFor="cleanupInterval">Интервал очистки (дни)</Label>
                <Input 
                  id="cleanupInterval"
                  type="number"
                  defaultValue="30"
                  className="w-32"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}